import { SofaClient } from "@oai/sofa-client";

import type { FetchFn } from "@oai/safe-http-client/types/client";

const SOFA_EXPECTED_CONTENT_TYPE = "application/json";
const SOFA_CANONICAL_FILTER_QUERY_MAX_BYTES = 12_288;
const SOFA_REQUEST_TARGET_MAX_BYTES_EXCLUSIVE = 16_384;
const SOFA_CANONICAL_FILTER_PATHS = new Set([
  "/v1/ui/campaigns",
  "/v1/ui/campaigns/totals",
  "/v1/ui/ad_groups",
  "/v1/ui/ad_groups/totals",
  "/v1/ui/ads",
  "/v1/ui/ads/totals",
]);

export type SofaClientRequestErrorReason =
  | "canonical_filters_too_large"
  | "non_success_status"
  | "missing_content_type"
  | "request_failed_before_response"
  | "request_target_too_large"
  | "unsupported_content_type";

type SofaClientRequestErrorDetails = {
  method: string;
  path: string;
  reason: SofaClientRequestErrorReason;
  response?: Pick<Response, "status" | "headers">;
};

export class SofaClientRequestError extends Error {
  readonly method: string;
  readonly path: string;
  readonly reason: SofaClientRequestErrorReason;

  // Response fields:
  readonly responseContentType?: string | null;
  readonly status?: number;
  readonly requestId?: string;

  constructor(message: string, details: SofaClientRequestErrorDetails, options?: ErrorOptions) {
    super(message, options);
    this.name = "SofaClientRequestError";
    this.method = details.method;
    this.path = details.path;
    this.reason = details.reason;
    this.responseContentType = details.response?.headers.get("Content-Type");
    this.status = details.response?.status;
    this.requestId = details.response?.headers.get("x-request-id") ?? undefined;
  }
}

export function createSofaClient(
  apiUrl: string,
  fetchFn: FetchFn = globalThis.fetch.bind(globalThis),
): SofaClient {
  return new SofaClient(apiUrl, {
    fetchFn: createInstrumentedSofaFetch(fetchFn),
    queryArrayParamStyle: "brackets",
  });
}

function createInstrumentedSofaFetch(fetchFn: FetchFn): FetchFn {
  return async (url, request) => {
    const method = getRequestMethod(request);
    const path = getRequestPath(url);

    try {
      assertCanonicalFilterRequestWithinSizeLimits(method, path, url);
      const response = await fetchFn(url, request);
      // The generated client rejects invalid response content types with generic errors,
      // so we assert here first to preserve method/path/status details in the thrown error.
      assertJsonResponse(method, path, response);
      return normalizeJsonResponseContentType(response);
    } catch (error) {
      if (error instanceof SofaClientRequestError) {
        throw error;
      }

      throw new SofaClientRequestError(
        `API service ${method} ${path} failed before receiving a response: ${getErrorMessage(error)}`,
        {
          method,
          path,
          reason: "request_failed_before_response",
        },
        error instanceof Error ? { cause: error } : undefined,
      );
    }
  };
}

function assertCanonicalFilterRequestWithinSizeLimits(method: string, path: string, url: string) {
  if (method !== "GET" || !SOFA_CANONICAL_FILTER_PATHS.has(path)) {
    return;
  }

  const parsed = parseUrl(url);
  if (!parsed) {
    return;
  }

  const canonicalFilterSegments = parsed.search
    .slice(1)
    .split("&")
    .filter(isCanonicalFilterQuerySegment);
  if (canonicalFilterSegments.length === 0) {
    return;
  }

  const canonicalFilterQueryBytes = getUtf8ByteLength(canonicalFilterSegments.join("&"));
  if (canonicalFilterQueryBytes > SOFA_CANONICAL_FILTER_QUERY_MAX_BYTES) {
    throw new SofaClientRequestError(
      `API service ${method} ${path} was not called because its canonical filters use ${canonicalFilterQueryBytes} encoded bytes (maximum ${SOFA_CANONICAL_FILTER_QUERY_MAX_BYTES})`,
      {
        method,
        path,
        reason: "canonical_filters_too_large",
      },
    );
  }

  const requestTargetBytes = getUtf8ByteLength(`${parsed.pathname}${parsed.search}`);
  if (requestTargetBytes >= SOFA_REQUEST_TARGET_MAX_BYTES_EXCLUSIVE) {
    throw new SofaClientRequestError(
      `API service ${method} ${path} was not called because its request target uses ${requestTargetBytes} encoded bytes (must be less than ${SOFA_REQUEST_TARGET_MAX_BYTES_EXCLUSIVE})`,
      {
        method,
        path,
        reason: "request_target_too_large",
      },
    );
  }
}

function parseUrl(url: string): URL | undefined {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
}

function isCanonicalFilterQuerySegment(segment: string): boolean {
  const separatorIndex = segment.indexOf("=");
  const encodedKey = separatorIndex === -1 ? segment : segment.slice(0, separatorIndex);

  try {
    const decodedKey = decodeURIComponent(encodedKey.replaceAll("+", " "));
    return decodedKey.split("[", 1)[0] === "filters";
  } catch {
    return false;
  }
}

function getUtf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function getRequestMethod(request?: RequestInit): string {
  return (request?.method ?? "GET").toUpperCase();
}

function getRequestPath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch {
    return url;
  }
}

function assertJsonResponse(method: string, path: string, response: Response) {
  const contentType = response.headers.get("Content-Type");

  if (!contentType) {
    throw new SofaClientRequestError(
      `API service ${method} ${path} returned HTTP ${response.status} without a Content-Type header`,
      {
        method,
        path,
        reason: "missing_content_type",
        response,
      },
    );
  }

  const mimeType = contentType.split(";", 1)[0]?.trim().toLowerCase();
  if (mimeType !== SOFA_EXPECTED_CONTENT_TYPE) {
    throw new SofaClientRequestError(
      `API service ${method} ${path} returned HTTP ${response.status} with unsupported content type ${contentType} (expected ${SOFA_EXPECTED_CONTENT_TYPE})`,
      {
        method,
        path,
        reason: "unsupported_content_type",
        response,
      },
    );
  }
}

function normalizeJsonResponseContentType(response: Response): Response {
  const contentType = response.headers.get("Content-Type");
  if (contentType === SOFA_EXPECTED_CONTENT_TYPE) {
    return response;
  }

  const headers = new Headers(response.headers);
  headers.set("Content-Type", SOFA_EXPECTED_CONTENT_TYPE);
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return String(error);
}
